import { MiniKit } from '@worldcoin/minikit-js';

/**
 * 發送輕度觸覺回饋
 * 適用於：小型UI元素點擊、按鈕點擊、輕微互動
 */
export const sendLightHapticFeedback = async (): Promise<void> => {
  try {
    if (typeof window !== 'undefined' && window.MiniKit && MiniKit.isInstalled()) {
      // 使用 MiniKit 的觸覺回饋（更豐富的體驗）
      await MiniKit.sendHapticFeedback({
        hapticsType: 'impact',
        style: 'light'
      });
      console.log('📳 MiniKit haptic feedback triggered: impact/light');
    } else if ('vibrate' in navigator && navigator.vibrate) {
      // 退回到標準震動 API - 輕度震動
      const vibrationSuccessful = navigator.vibrate(30);
      console.log('📳 Standard light vibration triggered:', vibrationSuccessful);
    } else {
      console.log('📳 Haptic feedback not supported on this device/environment');
    }
  } catch (hapticError) {
    console.log('⚠️ MiniKit haptic feedback failed, trying standard vibration:', hapticError);
    // 如果 MiniKit 觸覺回饋失敗，嘗試標準震動
    if ('vibrate' in navigator && navigator.vibrate) {
      try {
        const vibrationSuccessful = navigator.vibrate(30); // 30毫秒輕度震動
        console.log('📳 Fallback light vibration triggered:', vibrationSuccessful);
      } catch (vibrationError) {
        console.log('⚠️ All haptic feedback methods failed:', vibrationError);
      }
    }
  }
};

/**
 * 發送重度觸覺回饋
 * 適用於：重要操作確認、成功動作、強調性互動
 */
export const sendHeavyHapticFeedback = async (): Promise<void> => {
  try {
    if (typeof window !== 'undefined' && window.MiniKit && MiniKit.isInstalled()) {
      // 使用 MiniKit 的觸覺回饋（更豐富的體驗）
      await MiniKit.sendHapticFeedback({
        hapticsType: 'impact',
        style: 'heavy'
      });
      console.log('📳 MiniKit haptic feedback triggered: impact/heavy');
    } else if ('vibrate' in navigator && navigator.vibrate) {
      // 退回到標準震動 API - 重度震動
      const vibrationSuccessful = navigator.vibrate(100);
      console.log('📳 Standard heavy vibration triggered:', vibrationSuccessful);
    } else {
      console.log('📳 Haptic feedback not supported on this device/environment');
    }
  } catch (hapticError) {
    console.log('⚠️ MiniKit haptic feedback failed, trying standard vibration:', hapticError);
    // 如果 MiniKit 觸覺回饋失敗，嘗試標準震動
    if ('vibrate' in navigator && navigator.vibrate) {
      try {
        const vibrationSuccessful = navigator.vibrate(100); // 100毫秒重度震動
        console.log('📳 Fallback heavy vibration triggered:', vibrationSuccessful);
      } catch (vibrationError) {
        console.log('⚠️ All haptic feedback methods failed:', vibrationError);
      }
    }
  }
};

/**
 * 發送成功通知觸覺回饋
 * 適用於：成功操作、完成任務、積極結果
 */
export const sendSuccessHapticFeedback = async (): Promise<void> => {
  try {
    if (typeof window !== 'undefined' && window.MiniKit && MiniKit.isInstalled()) {
      // 使用 MiniKit 的通知類型觸覺回饋
      await MiniKit.sendHapticFeedback({
        hapticsType: 'notification',
        style: 'success'
      });
      console.log('📳 MiniKit haptic feedback triggered: notification/success');
    } else if ('vibrate' in navigator && navigator.vibrate) {
      // 退回到標準震動 API - 成功振動模式（短-短-長）
      const vibrationSuccessful = navigator.vibrate([50, 100, 50]);
      console.log('📳 Standard success vibration pattern triggered:', vibrationSuccessful);
    } else {
      console.log('📳 Haptic feedback not supported on this device/environment');
    }
  } catch (hapticError) {
    console.log('⚠️ MiniKit haptic feedback failed, trying standard vibration:', hapticError);
    if ('vibrate' in navigator && navigator.vibrate) {
      try {
        const vibrationSuccessful = navigator.vibrate([50, 100, 50]);
        console.log('📳 Fallback success vibration triggered:', vibrationSuccessful);
      } catch (vibrationError) {
        console.log('⚠️ All haptic feedback methods failed:', vibrationError);
      }
    }
  }
};

/**
 * 發送錯誤通知觸覺回饋
 * 適用於：錯誤操作、失敗結果、警告提示
 */
export const sendErrorHapticFeedback = async (): Promise<void> => {
  try {
    if (typeof window !== 'undefined' && window.MiniKit && MiniKit.isInstalled()) {
      // 使用 MiniKit 的通知類型觸覺回饋
      await MiniKit.sendHapticFeedback({
        hapticsType: 'notification',
        style: 'error'
      });
      console.log('📳 MiniKit haptic feedback triggered: notification/error');
    } else if ('vibrate' in navigator && navigator.vibrate) {
      // 退回到標準震動 API - 錯誤振動模式（長-短-長）
      const vibrationSuccessful = navigator.vibrate([100, 50, 100]);
      console.log('📳 Standard error vibration pattern triggered:', vibrationSuccessful);
    } else {
      console.log('📳 Haptic feedback not supported on this device/environment');
    }
  } catch (hapticError) {
    console.log('⚠️ MiniKit haptic feedback failed, trying standard vibration:', hapticError);
    if ('vibrate' in navigator && navigator.vibrate) {
      try {
        const vibrationSuccessful = navigator.vibrate([100, 50, 100]);
        console.log('📳 Fallback error vibration triggered:', vibrationSuccessful);
      } catch (vibrationError) {
        console.log('⚠️ All haptic feedback methods failed:', vibrationError);
      }
    }
  }
};

/**
 * 發送選擇變更觸覺回饋
 * 適用於：選項切換、checkbox點擊、狀態變更
 */
export const sendSelectionChangedHapticFeedback = async (): Promise<void> => {
  try {
    if (typeof window !== 'undefined' && window.MiniKit && MiniKit.isInstalled()) {
      // 使用 MiniKit 的選擇變更類型觸覺回饋
      await MiniKit.sendHapticFeedback({
        hapticsType: 'selectionChanged'
      });
      console.log('📳 MiniKit haptic feedback triggered: selectionChanged');
    } else if ('vibrate' in navigator && navigator.vibrate) {
      // 退回到標準震動 API - 選擇變更振動（短震動）
      const vibrationSuccessful = navigator.vibrate(25);
      console.log('📳 Standard selection changed vibration triggered:', vibrationSuccessful);
    } else {
      console.log('📳 Haptic feedback not supported on this device/environment');
    }
  } catch (hapticError) {
    console.log('⚠️ MiniKit haptic feedback failed, trying standard vibration:', hapticError);
    if ('vibrate' in navigator && navigator.vibrate) {
      try {
        const vibrationSuccessful = navigator.vibrate(25);
        console.log('📳 Fallback selection changed vibration triggered:', vibrationSuccessful);
      } catch (vibrationError) {
        console.log('⚠️ All haptic feedback methods failed:', vibrationError);
      }
    }
  }
};

/**
 * 便捷的觸覺回饋函數集合
 * 提供更語意化的函數名稱
 */
export const hapticFeedback = {
  // 基本互動
  click: sendLightHapticFeedback,
  tap: sendLightHapticFeedback,
  button: sendLightHapticFeedback,
  
  // 重要操作
  confirm: sendHeavyHapticFeedback,
  submit: sendHeavyHapticFeedback,
  action: sendHeavyHapticFeedback,
  
  // 狀態回饋
  success: sendSuccessHapticFeedback,
  error: sendErrorHapticFeedback,
  toggle: sendSelectionChangedHapticFeedback,
  select: sendSelectionChangedHapticFeedback,
  
  // 原始函數
  light: sendLightHapticFeedback,
  heavy: sendHeavyHapticFeedback
};
